import { useState, useEffect } from 'react';
import { auth } from '../../firebase/config';
import { useHistory } from "react-router-dom";
import Modal from 'react-modal';
import ButtonClicked from '../../hooks/ButtonClicked'
import spinner from '../../images/spinner-ripple.svg'
import safeNextPath from '../../helpers/auth/safeNextPath'
import recordLogin from '../../helpers/auth/recordLogin'
import fetchLoginProfile from '../../helpers/auth/fetchLoginProfile'
import resetPasswordMessage from '../../helpers/auth/resetPasswordMessage'
import normalizeEmail from '../../helpers/normalizeEmail'
import loginMessage from '../../helpers/auth/loginMessage'
import profileFetchMessage from '../../helpers/auth/profileFetchMessage'
import { normalizeTotpCode, totpHint, totpMessage, needsMfaSetup } from '../../helpers/auth/totp'
import { TotpMultiFactorGenerator } from '../../firebase/modularAuth'

const Login = () => {

    const [loading, setLoading] = useState(false)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [modalOpen, setModalOpen] = useState(false)
    const [emailReset, setEmailReset] = useState('')
    const [errorReset, setErrorReset] = useState('')
    const [succesReset, setSuccesReset] = useState('')

    // De melding op het scherm zelf, in plaats van een `alert`. Een popup die
    // uit `onAuthStateChanged` komt, keert bij ELKE herlading terug: wie
    // ingelogd raakte maar niet doorgestuurd werd, zat vast in diezelfde
    // melding tot de browser aanbood hem te onderdrukken -- en hield dan een
    // stil, dood scherm over. Gemeld door Duurzame Vecht (2026-09-23).
    // `herstelbaar` bepaalt of de knoppen 'Opnieuw proberen' en 'Uitloggen'
    // erbij staan: bij een fout wachtwoord hoef je nergens uit te loggen.
    const [melding, setMelding] = useState('')
    const [herstelbaar, setHerstelbaar] = useState(false)
    // Wie er inlogde toen het ophalen mislukte. Bewaren en niet `auth.currentUser`
    // lezen: dat is een momentopname van de SDK die vlak na een herlading nog
    // leeg kan zijn, en dan doet 'Opnieuw proberen' niets.
    const [vastgelopenUser, setVastgelopenUser] = useState(null)

    // De tweede stap: na een goed wachtwoord vraagt Firebase om de code uit de
    // authenticator-app. De resolver komt mee op de fout van
    // signInWithEmailAndPassword; null = geen tweede stap bezig.
    // AI-CONTEXT: See docs/features/mfa-totp.md
    const [mfaResolver, setMfaResolver] = useState(null)
    const [mfaHint, setMfaHint] = useState(null)
    const [totpCode, setTotpCode] = useState('')

    const history = useHistory();
    Modal.setAppElement('#root')

    const modalStyles = {
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-10%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: 'auto'
        },
      };

    const emailHandler = (e) => {
        const email = e.target.value
        setEmail(email)
    }

    const passwordHandler = (e) => {
        const password = e.target.value
        setPassword(password)
    }

    const loginHandler = (e) => {
        e.preventDefault()
        e.target.innerText = 'Inloggen'
        setLoading(true)

        // Een geplakt adres sleept routineus een spatie mee die geen enkel
        // scherm toont, en Firebase vergelijkt byte-exact. Het wachtwoord gaat
        // bewust ongemoeid mee -- daar kan een spatie in horen.
        // AI-CONTEXT: See docs/pitfalls.md#database-untrimmed-email-breaks-exact-match
        setMelding('')
        setHerstelbaar(false)

        auth.signInWithEmailAndPassword(normalizeEmail(email), password)
        .catch(err => {
            setLoading(false)
            setHerstelbaar(false)
            if (err?.code === 'auth/multi-factor-auth-required') {
                startTweedeStap(err.resolver)
                return
            }
            // De ruwe fout van Firebase is Engels ('E-mailadres is badly
            // formatted') en zegt niet wat je eraan moet doen.
            setMelding(loginMessage(err?.code))
        })
    }

    /** Wachtwoord klopte; nu de code uit de authenticator-app vragen. */
    const startTweedeStap = (resolver) => {
        const hint = totpHint(resolver?.hints)
        if (!hint) {
            // Alleen een sms-factor (bestaat technisch, Deccos biedt hem niet
            // aan). Geen codeveld tonen dat nooit kan werken.
            setMelding('Dit account gebruikt een andere tweede stap dan een authenticator-app. Neem contact op met Deccos via info@deccos.nl.')
            return
        }
        setMfaResolver(resolver)
        setMfaHint(hint)
        setTotpCode('')
    }

    /** Terug naar e-mailadres en wachtwoord. */
    const stopTweedeStap = () => {
        setMfaResolver(null)
        setMfaHint(null)
        setTotpCode('')
        setMelding('')
        setLoading(false)
    }

    const codeHandler = (e) => {
        const code = e.target.value
        setTotpCode(code)
    }

    // Lukt dit, dan vuurt onAuthStateChanged en stuurt automaticRedirect door;
    // de spinner blijft dus staan.
    const codeHandlerSubmit = (e) => {
        e.preventDefault()
        const code = normalizeTotpCode(totpCode)
        if (!code) {
            setMelding('Vul de zes cijfers uit je authenticator-app in.')
            return
        }
        setMelding('')
        setLoading(true)
        mfaResolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(mfaHint.uid, code))
        .catch(err => {
            setLoading(false)
            setMelding(totpMessage(err?.code))
            // De inlogpoging is verlopen: de resolver is waardeloos geworden.
            if (err?.code === 'auth/invalid-multi-factor-session' || err?.code === 'auth/missing-multi-factor-session') {
                setMfaResolver(null)
                setMfaHint(null)
            }
        })
    }

     // Login on Enter key
     useEffect(() => {
        // Attach the keypress event listener to the document
        document.addEventListener('keypress', enterKeyLogin);
    
        // Clean up the event listener when the component unmounts
        return () => {
          document.removeEventListener('keypress', enterKeyLogin);
        };
      }, [email, password, mfaResolver, totpCode]);

    
     const enterKeyLogin = (e) => {
        if(e.key !== 'Enter') return
        // In de tweede stap verstuurt Enter de code, niet opnieuw het wachtwoord.
        if (mfaResolver) {
            codeHandlerSubmit(e)
            return
        }
        loginHandler(e)
        setLoading(true)
    }

    const automaticRedirect = async (User) => {

        // MFA fase 5: verplicht maar nog geen tweede stap → de regels weigeren
        // het eigen profiel, en ophalen zou 'profiel ophalen mislukt' geven.
        // MfaGate toont op dit scherm het koppelscherm.
        // AI-CONTEXT: See docs/features/mfa-totp.md (fase 5)
        const { claims } = await User.getIdTokenResult()
        if (needsMfaSetup(claims)) {
            setLoading(false)
            return
        }

        // Waar moet deze gebruiker heen? Rechtstreeks uit Firestore in plaats
        // van via de Cloud Function `userInfoLogin`, die dezelfde read deed
        // maar koud 3,3 seconde nodig had. AI-CONTEXT: docs/inbox.md Bug #221
        let profile
        try {
            profile = await fetchLoginProfile(User?.uid)
        } catch (err) {
            // Het ophalen mislukte — dat is iets anders dan een ontbrekend
            // profiel en kan tijdelijk zijn. Niet uitloggen: dan zou een
            // storing van tien seconden iedereen eruit gooien met een melding
            // die niet klopt. Zie docs/inbox.md Bug #137.
            console.error('Ophalen van het profiel mislukte:', err)
            setLoading(false)
            // De code hoort in de melding zelf, niet alleen in de console: een
            // vastgelopen gebruiker stuurt een schermafbeelding, en daar stond
            // voor elke oorzaak dezelfde zin op. 'Geen verbinding' en 'sessie
            // verlopen' vragen om een andere handeling.
            setMelding(profileFetchMessage(err?.code))
            setHerstelbaar(true)
            setVastgelopenUser(User)
            return
        }

        const id = profile?.id
        const organisations = profile?.organisations
        const verified = profile?.verified

        // Een account kan bestaan zonder profiel: dan lukt inloggen wel, maar
        // hoort het bij geen enkele organisatie en is er niets om naartoe te
        // sturen. Voorheen viel de pagina hier stil — geen doorverwijzing, geen
        // melding — en leek het alsof het wachtwoord fout was. Elke reset liep
        // tegen dezelfde muur. Zie docs/inbox.md Bug #137.
        //
        // `profileMissing` is het expliciete antwoord; de losse velden blijven
        // meedoen omdat een profiel ook leeg of half ingevuld kan zijn.
        if (profile?.profileMissing === true || !id || !organisations?.[0]) {
            setLoading(false)
            await auth.signOut()
            alert(
                `Inloggen is gelukt, maar dit account is nog niet gekoppeld aan een organisatie. `
                + `Vraag de beheerder van je organisatie om een nieuwe uitnodiging, `
                + `of neem contact op met Deccos via info@deccos.nl.`
            )
            return
        }

        // De teller staat bewust vóór de doorverwijzing: het herladen hieronder
        // breekt een nog lopende schrijfactie af, dus fire-and-forget zou hem
        // stilletjes laten vallen. recordLogin schrijft rechtstreeks naar
        // Firestore (~100 ms) in plaats van via de Cloud Function
        // `updateLoginCounter`, die koud 3 tot 6 seconden nodig had. Hij gooit
        // nooit — een mislukte teller mag het inloggen niet blokkeren.
        // AI-CONTEXT: See docs/inbox.md Bug #221
        if (verified === true && organisations?.[0]) {
            await recordLogin({ userDocId: User?.uid, compagnyId: organisations[0] })
        }

        // De pagina waar de gebruiker heen wilde voordat hij hier belandde --
        // bijvoorbeeld de link uit een updatemail. Die staat in de URL en niet
        // in state, want alleen de URL overleeft de herlaadstap hieronder.
        // safeNextPath weigert alles buiten deze site en elke organisatie
        // waar deze gebruiker geen lid van is.
        // AI-CONTEXT: See docs/features/updates-mailing.md
        const next = safeNextPath(
            new URLSearchParams(window.location.search).get('next'),
            organisations
        )

        // Redirect to correct page
        //
        // Het herladen is dragend en mag niet weg: `src/hooks/Client.js` leest
        // de organisatie uit de URL op het moment dat de module geladen wordt,
        // dus zonder herladen matcht geen enkele /:client-route.
        if(verified === false){
            // Een niet-geverifieerd account moet eerst zijn account verifiëren;
            // een bestemming uit een mail gaat daar niet voor.
            history.push(`/${organisations[0]}/NotApproved/${id}`)
            window.location.reload()
        } else if(verified === true){
            history.push(next || `/${organisations[0]}/dashboard`)
            window.location.reload()
        }
    }

    useEffect(() => {
        // Auto-redirect when a session already exists (user opens /login but
        // is still signed in). Guard against firing without a User — anders
        // krijgt fetchLoginProfile een undefined uid en weigert hij.
        const unsub = auth.onAuthStateChanged((User) => {
            if (User) automaticRedirect(User)
        })
        return () => unsub()
    },[])

    /** Opnieuw proberen met de sessie die er al is. */
    const opnieuwProberen = async () => {
        setMelding('')
        setHerstelbaar(false)
        setLoading(true)
        const User = vastgelopenUser || auth.currentUser
        if (!User) {
            // De sessie is intussen weg; dan is gewoon opnieuw inloggen het
            // antwoord en niet nog een poging met niets.
            setLoading(false)
            return
        }
        await automaticRedirect(User)
    }

    /** Uit de half-ingelogde toestand stappen. */
    const uitloggen = async () => {
        await auth.signOut()
        setMelding('')
        setHerstelbaar(false)
        setVastgelopenUser(null)
        setLoading(false)
    }

    const resetPasswordModal = () => {

        setModalOpen(true)

    }

    const closeModal = () => {
        setModalOpen(false);
    }

    const emailResetHandler = (e) => {
        const email = e.target.value 

        setEmailReset(email)
    }

    // Elke afloop moet iets op het scherm zetten. Voorheen ving alleen
    // `auth/user-not-found` iets af en viel elke andere code in een lege
    // else-if, terwijl ButtonClicked de knop al op 'Verzonden' had gezet. Een
    // geblokkeerde poging (`auth/too-many-requests` na een paar keer klikken)
    // zag er dus precies zo uit als een verstuurde mail, en de gebruiker bleef
    // wachten op iets dat nooit was aangevraagd.
    // AI-CONTEXT: See docs/inbox.md Bug #222
    const resetPassword = (e) => {

        const address = normalizeEmail(emailReset)

        // De vorige uitkomst hoort niet naast de nieuwe te blijven staan.
        setErrorReset('')
        setSuccesReset('')

        if (!address) {
            setErrorReset('Vul je emailadres in.')
            return
        }

        ButtonClicked(e, 'Verzonden')

        auth.sendPasswordResetEmail(address)
        .then(() => {
            setSuccesReset(`Er is een email verstuurd naar ${address} waarmee je je wachtwoord kunt resetten.`)
        })
        .catch((error) => {
            setErrorReset(resetPasswordMessage(error?.code))
            resetResetButton(e)
        });
    }

    const resetResetButton = (e) => {
        setTimeout(() => {
            e.target.innerText = 'Verzenden' 
            e.target.style.borderColor = 'green'
            e.target.style.color = 'green'
        }, 3000)
    }

    return (
    <div className="login-container">
        <h1>Login</h1>
        {/* Nooit native verzenden: Enter en de knoppen lopen via de handlers,
            en een herlading midden in de tweede stap breekt het inloggen af. */}
        <form id="login-form" onSubmit={(e) => e.preventDefault()}>
            {mfaResolver ? (
                <>
                    <label htmlFor='totp-code'>Code uit je authenticator-app</label>
                    <input id='totp-code' onChange={codeHandler} value={totpCode} type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" />
                </>
            ) : (
                <>
                    <p>Email</p>
                    <input onChange={emailHandler} value={email} type="email" placeholder="Schrijf hier je emailadres" />
                    <p>Wachtwoord</p>
                    <input onChange={passwordHandler} value={password} type="password" placeholder="Schrijf hier je wachtwoord" />
                </>
            )}
            <p id='password-reset-button-modal' onClick={resetPasswordModal}>Wachtwoord vergeten? Klik hier.</p>
            <Modal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                style={modalStyles}
                contentLabel="Upload file"
            >
                <h2>Reset wachtwoord</h2>
                <p>Jouw emailadres</p>
                <input type="text" placeholder='Schrijf hier je emailadres' onChange={emailResetHandler}/>
                <p id='error-message-reset-password'>{errorReset}</p>
                <p id='succes-message-password-reset'>{succesReset}</p>
                <button id='button-reset-password' onClick={resetPassword}>Versturen</button>
            </Modal>
            {melding &&
                // role="alert": dit verschijnt NA een handeling van de
                // gebruiker, dus een schermlezer hoort het te melden.
                <div className="login-message" role="alert">
                    <p>{melding}</p>
                    {herstelbaar &&
                        <div className="login-message-actions">
                            <button type="button" onClick={opnieuwProberen}>Opnieuw proberen</button>
                            <button type="button" onClick={uitloggen}>Uitloggen</button>
                        </div>
                    }
                </div>
            }
            <div className="button-container button-login">
                {mfaResolver ? (
                    !loading && <>
                        <button type="button" onClick={codeHandlerSubmit}>Bevestigen</button>
                        <button type="button" onClick={stopTweedeStap}>Annuleren</button>
                    </>
                ) : (
                    <button onClick={loginHandler} onKeyPress={enterKeyLogin} style={{display: loading ? 'none' : 'block'}}>Login</button>
                )}
                <img src={spinner} alt="" style={{display: loading ? 'block' : 'none'}}/>
            </div>
        </form>
    </div>
    )
}

export default Login